import type { IntlShape } from 'react-intl';

import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import { DateUtil } from '@src/packages/utils/dates';

export type AttendanceMarkingRow = {
  key: 'start' | 'breakStart' | 'breakEnd' | 'end';
  labelId: string;
  value: string;
};

type BuildAttendanceMarkingRowsOptions = {
  /** When true, every mark row shows the absent label + date. */
  isAbsent?: boolean;
  /** ISO date (YYYY-MM-DD) used for the absent label. */
  absentDate?: string | null;
};

export const formatAttendanceMarkDateTime = (intl: IntlShape, ms: number | null): string => {
  if (ms == null) return '—';
  return intl.formatMessage(
    { id: 'workJourney.management.matrix.attendance.markDateTime' },
    {
      time: DateUtil.toDateString(ms, DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT),
      date: DateUtil.toDateString(ms, DateUtil.DEFAULT_DT_DATE_FORMAT),
    },
  );
};

export const formatAttendanceAbsentMark = (intl: IntlShape, isoDate?: string | null): string => {
  const ms = isoDate
    ? DateUtil.getTimestampMs(`${isoDate}T12:00:00`) ?? Date.parse(`${isoDate}T12:00:00`)
    : null;
  const date = Number.isFinite(ms)
    ? DateUtil.toDateString(ms as number, DateUtil.DEFAULT_DT_DATE_FORMAT)
    : '—';
  return intl.formatMessage(
    { id: 'workJourney.management.matrix.attendance.markAbsent' },
    { date },
  );
};

export const buildAttendanceMarkingRows = (
  intl: IntlShape,
  record: IAttendanceRecord | null,
  options: BuildAttendanceMarkingRowsOptions = {},
): AttendanceMarkingRow[] => {
  const defs: Array<{
    key: AttendanceMarkingRow['key'];
    labelId: string;
    field: IAttendanceRecord['actualStartAt'];
  }> = [
    {
      key: 'start',
      labelId: 'workJourney.management.matrix.attendance.mark.start',
      field: record?.actualStartAt,
    },
    {
      key: 'breakStart',
      labelId: 'workJourney.management.matrix.attendance.mark.breakStart',
      field: record?.actualBreakStartAt,
    },
    {
      key: 'breakEnd',
      labelId: 'workJourney.management.matrix.attendance.mark.breakEnd',
      field: record?.actualBreakEndAt,
    },
    {
      key: 'end',
      labelId: 'workJourney.management.matrix.attendance.mark.end',
      field: record?.actualEndAt,
    },
  ];

  if (options.isAbsent) {
    const value = formatAttendanceAbsentMark(intl, options.absentDate);
    return defs.map(def => ({
      key: def.key,
      labelId: def.labelId,
      value,
    }));
  }

  return defs.map(def => ({
    key: def.key,
    labelId: def.labelId,
    value: formatAttendanceMarkDateTime(intl, DateUtil.getTimestampMs(def.field)),
  }));
};

export const formatAttendanceSectionDate = (intl: IntlShape, isoDate: string): string => {
  const ms = DateUtil.getTimestampMs(`${isoDate}T12:00:00`) ?? Date.parse(`${isoDate}T12:00:00`);
  if (!Number.isFinite(ms)) return isoDate;
  const weekday = intl.formatDate(ms, { weekday: 'long' });
  const dayMonth = DateUtil.toDateString(ms, 'DD/MM');
  return `${weekday} ${dayMonth}`;
};
